/* =========================================================
  quantity-selector-kouza + variant stock display (optimized)
========================================================= */

(() => {
  const OBSERVER_FLAG = "__kouzaUnifiedObserver__";
  const HARD_MAX_QTY = 5;

  const formUiStateMap = new WeakMap();

  const pageState = {
    loaded: document.readyState === "complete",
  };

  if (!pageState.loaded) {
    window.addEventListener(
      "load",
      () => {
        pageState.loaded = true;
        document.dispatchEvent(new CustomEvent("kouza:page-loaded"));
      },
      { once: true },
    );
  }

  function toInt(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function getFormRoot(el) {
    if (!el) return null;
    return el.form || el.closest("form");
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(String(value));
    }
    return String(value).replace(/"/g, '\\"');
  }

  function getAddToCartButtons(formRoot) {
    if (!formRoot) return [];
    return Array.from(
      formRoot.querySelectorAll(
        "button.add-to-cart, button[name='add'], [data-add-to-cart]",
      ),
    );
  }

  function getFormUiState(formRoot) {
    let state = formUiStateMap.get(formRoot);

    if (!state) {
      state = {
        uid: Math.random().toString(36).slice(2),
        reasons: {},
        reasonEls: {},
      };
      formUiStateMap.set(formRoot, state);
    }

    return state;
  }

  function ensureReasonEl(formRoot, reasonKey, defaultMessage) {
    const state = getFormUiState(formRoot);
    const existing = state.reasonEls[reasonKey];

    if (existing && existing.isConnected) return existing;

    const buttons = getAddToCartButtons(formRoot);
    const anchor = buttons[0] || formRoot;

    const el = document.createElement("p");
    el.setAttribute("data-kouza-atc-error", reasonKey);
    el.setAttribute("role", "alert");
    el.setAttribute("aria-live", "polite");
    el.id = `kouza-atc-error-${state.uid}-${reasonKey}`;
    el.style.display = "none";
    el.textContent = defaultMessage || "";

    if (anchor.parentElement) {
      anchor.parentElement.insertBefore(el, anchor);
    } else {
      formRoot.appendChild(el);
    }

    state.reasonEls[reasonKey] = el;
    return el;
  }

  function applyAtcReason(formRoot, reasonKey, disabled, message) {
    if (!formRoot) return;

    const state = getFormUiState(formRoot);
    state.reasons[reasonKey] = {
      disabled: !!disabled,
      message: message || "",
    };

    const visibleIds = [];

    Object.entries(state.reasons).forEach(([key, reason]) => {
      const el = ensureReasonEl(formRoot, key, reason.message || "");

      if (reason.disabled && reason.message) {
        el.textContent = reason.message;
        el.style.display = "block";
        visibleIds.push(el.id);
      } else {
        el.style.display = "none";
      }
    });

    const shouldDisable = Object.values(state.reasons).some(
      (reason) => reason && reason.disabled,
    );

    getAddToCartButtons(formRoot).forEach((btn) => {
      btn.disabled = shouldDisable;
      btn.setAttribute("aria-disabled", shouldDisable ? "true" : "false");
      btn.classList.toggle("is-kouza-disabled", shouldDisable);

      if (visibleIds.length) {
        btn.setAttribute("aria-describedby", visibleIds.join(" "));
      } else {
        btn.removeAttribute("aria-describedby");
      }
    });
  }

  function isVariantRelatedTarget(target) {
    if (!target || !target.closest) return false;

    return !!target.closest(
      [
        'select[name="id"]',
        'input[name="id"]',
        'select[name^="options["]',
        'input[name^="options["]',
        "[data-single-option-selector]",
        "[data-variant-input]",
        "[data-option-position]",
        "[data-option-index]",
        '[data-index^="option"]',
        ".variant-input-wrap",
        ".variant-input",
        ".product-form__input",
        "variant-selects",
        "variant-radios",
        "fieldset",
      ].join(", "),
    );
  }

  function getVariantScope(root, formRoot) {
    const candidates = [
      formRoot?.closest?.("[data-product-blocks]"),
      formRoot?.closest?.(".product-single__meta"),
      formRoot?.closest?.(".product__info-container"),
      formRoot?.closest?.(".product__info-wrapper"),
      root?.closest?.("[data-product-blocks]"),
      root?.closest?.(".product-single__meta"),
      root?.closest?.(".product__info-container"),
      root?.closest?.(".product__info-wrapper"),
      formRoot,
      root,
    ];

    return candidates.find(Boolean) || document.body;
  }

  function getVariantWatchTargets(scopeRoot, formRoot) {
    const selector = [
      'select[name="id"]',
      'input[name="id"]',
      'select[name^="options["]',
      'input[name^="options["]',
      "[data-single-option-selector]",
      "[data-variant-input]",
      "[data-option-position]",
      "[data-option-index]",
      '[data-index^="option"]',
      ".variant-input-wrap",
      ".variant-input",
      ".product-form__input",
      "variant-selects",
      "variant-radios",
      "fieldset",
    ].join(", ");

    const roots = [scopeRoot, formRoot].filter(Boolean);
    const set = new Set();

    roots.forEach((root) => {
      if (!(root instanceof Element)) return;

      if (root.matches(selector)) {
        set.add(root);
      }

      root.querySelectorAll(selector).forEach((el) => {
        set.add(
          el.closest(
            ".variant-input-wrap, .variant-input, .product-form__input, fieldset",
          ) || el,
        );
      });
    });

    if (!set.size && formRoot) {
      set.add(formRoot);
    }

    return Array.from(set);
  }

  function parseVariantStockJson(root) {
    const jsonEl = root.querySelector("[data-variant-stock-json]");
    if (!jsonEl) return [];

    try {
      const data = JSON.parse(jsonEl.textContent || "[]");
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  function getVariantIdFromUrl() {
    try {
      const url = new URL(window.location.href);
      return String(url.searchParams.get("variant") || "").trim();
    } catch {
      return "";
    }
  }

  function hasOptionSelectors(scopeEl) {
    if (!scopeEl) return false;

    return !!scopeEl.querySelector(
      [
        'select[name^="options["]',
        'input[name^="options["]',
        "[data-option-position]",
        "[data-option-index]",
        '[data-index^="option"]',
        ".variant-input-wrap",
        ".variant-input",
        ".product-form__input",
      ].join(", "),
    );
  }

  function normalizeOptionValue(value) {
    return String(value == null ? "" : value)
      .replace(/\s+/g, " ")
      .trim();
  }

  function getOptionGroupOrder(el, fallbackIndex) {
    const raw =
      el.getAttribute("data-option-position") ||
      el.getAttribute("data-option-index") ||
      el.getAttribute("data-index") ||
      "";

    const match = String(raw).match(/(\d+)/);
    return match ? Number(match[1]) : fallbackIndex + 1;
  }

  function getSelectedButtonLikeValue(group) {
    if (!group) return "";

    const active = group.querySelector(
      [
        '[aria-selected="true"]',
        '[aria-checked="true"]',
        '[aria-pressed="true"]',
        ".is-active",
        ".active",
        ".selected",
        ".current",
      ].join(", "),
    );

    if (!active) return "";

    return normalizeOptionValue(
      active.getAttribute("data-value") ||
        active.dataset.value ||
        active.getAttribute("value") ||
        active.textContent,
    );
  }

  function getOptionValues(scopeEl) {
    if (!scopeEl) return [];

    const selects = Array.from(
      scopeEl.querySelectorAll('select[name^="options["]'),
    );
    if (selects.length) {
      return selects
        .map((el) => normalizeOptionValue(el.value))
        .filter(Boolean);
    }

    const radios = Array.from(
      scopeEl.querySelectorAll('input[type="radio"][name^="options["]'),
    );
    if (radios.length) {
      const names = Array.from(new Set(radios.map((el) => el.name)));

      return names
        .map((name) => {
          const escaped = cssEscape(name);
          return scopeEl.querySelector(
            `input[type="radio"][name="${escaped}"]:checked`,
          );
        })
        .map((el) => normalizeOptionValue(el ? el.value : ""))
        .filter(Boolean);
    }

    const groups = Array.from(
      scopeEl.querySelectorAll(
        [
          "[data-option-position]",
          "[data-option-index]",
          '[data-index^="option"]',
          ".variant-input-wrap",
          ".variant-input",
          ".product-form__input",
          "fieldset",
        ].join(", "),
      ),
    )
      .filter((group) => {
        if (!group || !group.querySelector) return false;
        if (group.closest(".js-qty__wrapper")) return false;
        if (
          group.querySelector(
            'input[name^="options["], select[name^="options["]',
          )
        ) {
          return false;
        }

        return !!group.querySelector(
          [
            '[aria-selected="true"]',
            '[aria-checked="true"]',
            '[aria-pressed="true"]',
            ".is-active",
            ".active",
            ".selected",
            ".current",
            "[data-value]",
          ].join(", "),
        );
      })
      .map((group, index) => ({
        group,
        order: getOptionGroupOrder(group, index),
      }))
      .sort((a, b) => a.order - b.order);

    return groups
      .map(({ group }) => getSelectedButtonLikeValue(group))
      .filter(Boolean);
  }

  function getVariantOptionCount(variants) {
    if (!Array.isArray(variants) || !variants.length) return 0;

    return variants.reduce((count, variant) => {
      if (variant.option3 != null && String(variant.option3) !== "") return 3;
      if (variant.option2 != null && String(variant.option2) !== "") {
        return Math.max(count, 2);
      }
      if (variant.option1 != null && String(variant.option1) !== "") {
        return Math.max(count, 1);
      }
      return count;
    }, 0);
  }

  function findVariantIdByOptions(variants, optionValues) {
    if (!Array.isArray(variants) || !variants.length) return "";
    if (!optionValues.length) return "";

    const optionCount = getVariantOptionCount(variants);
    if (optionCount > 0 && optionValues.length < optionCount) {
      return "";
    }

    const normalizedOptions = optionValues.map(normalizeOptionValue);
    const [o1, o2, o3] = normalizedOptions;

    const hits = variants.filter((variant) => {
      if (o1 && normalizeOptionValue(variant.option1) !== o1) return false;
      if (o2 && normalizeOptionValue(variant.option2) !== o2) return false;
      if (o3 && normalizeOptionValue(variant.option3) !== o3) return false;
      return true;
    });

    return hits.length === 1 ? String(hits[0].id) : "";
  }

  function findIdInput(formRoot, scopeEl) {
    const roots = [formRoot, scopeEl].filter(Boolean);

    for (const root of roots) {
      const found =
        root.querySelector('select[name="id"]') ||
        root.querySelector('input[name="id"]');

      if (found) return found;
    }

    return null;
  }

  function resolveCurrentVariantId(
    scopeEl,
    formRoot,
    variants,
    root,
    { allowInitialFallback = false, preferOptions = false } = {},
  ) {
    const variantIds = new Set(
      (variants || []).map((variant) => String(variant.id)),
    );
    const scopeHasOptions = hasOptionSelectors(scopeEl);
    const idInput = findIdInput(formRoot, scopeEl);

    const idValue =
      idInput && variantIds.has(String(idInput.value || "").trim())
        ? String(idInput.value || "").trim()
        : "";

    if (scopeHasOptions) {
      const optionValues = getOptionValues(scopeEl);
      const byOptions = findVariantIdByOptions(variants, optionValues);

      if (preferOptions) {
        if (byOptions) {
          if (!idValue || idValue === byOptions) {
            return String(byOptions);
          }
          return "";
        }

        if (optionValues.length > 0) {
          return "";
        }
      } else if (byOptions) {
        if (!idValue || idValue === byOptions) {
          return String(byOptions);
        }
      }
    }

    if (idValue) {
      return idValue;
    }

    const byUrl = getVariantIdFromUrl();
    if (byUrl && variantIds.has(byUrl)) {
      return byUrl;
    }

    if (allowInitialFallback && root) {
      const initialId = String(
        root.getAttribute("data-initial-variant-id") || "",
      ).trim();

      if (initialId && variantIds.has(initialId)) {
        return initialId;
      }
    }

    return "";
  }

  function renderStockLoading(stockEl) {
    if (!stockEl) return;
    stockEl.style.display = "block";
    stockEl.textContent = "残席を確認しています。少々お待ちください。";
  }

  function hideStockDisplay(stockEl) {
    if (!stockEl) return;
    stockEl.style.display = "none";
  }

  function renderStockText(stockEl, variant) {
    if (!stockEl) return;

    if (!variant) {
      renderStockLoading(stockEl);
      return;
    }

    const isManagedByShopify = variant.inventory_management === "shopify";
    const canContinueSelling = variant.inventory_policy === "continue";

    if (!isManagedByShopify || canContinueSelling) {
      stockEl.style.display = "block";
      stockEl.textContent = "";
      return;
    }

    const remaining = Math.max(0, toInt(variant.inventory_quantity, 0));

    // 親子WSタグがある場合は「組」、それ以外は「名」
    const qtyRoot = stockEl.closest("[data-kouza-qty-root]");
    const unitLabel = qtyRoot && qtyRoot.dataset.familyFriendly === "1" ? "組" : "名";

    stockEl.style.display = "block";

    if (remaining > 0) {
      stockEl.innerHTML = `残り<span data-stock-num>${remaining}</span>${unitLabel}`;
    } else {
      stockEl.innerHTML =
        '<span class="stock-count__soldout">満席のため追加できません。</span>';
    }
  }

  function hasTrackedStockVariant(variants) {
    if (!Array.isArray(variants) || !variants.length) return false;

    return variants.some((variant) => {
      return (
        variant.inventory_management === "shopify" &&
        variant.inventory_policy !== "continue"
      );
    });
  }

  function calcDynamicMax(baseMax, variant) {
    let maxQty = baseMax;

    if (!variant) return maxQty;

    const isManagedByShopify = variant.inventory_management === "shopify";
    const canContinueSelling = variant.inventory_policy === "continue";

    if (isManagedByShopify && !canContinueSelling) {
      const remaining = Math.max(0, toInt(variant.inventory_quantity, 0));
      maxQty = Math.min(maxQty, remaining);
    }

    return maxQty;
  }

  function setQtyPendingState(root, { loading = false, soldOut = false } = {}) {
    if (!root) return;

    const inputId = root.getAttribute("data-qty-input-id");
    const input =
      (inputId && document.getElementById(inputId)) ||
      root.querySelector("[data-kouza-qty-input]");

    const minusBtn = root.querySelector("[data-kouza-qty-minus]");
    const plusBtn = root.querySelector("[data-kouza-qty-plus]");
    const controls = [minusBtn, plusBtn].filter(Boolean);

    if (loading) {
      if (input) {
        input.readOnly = true;
        input.disabled = false;
        input.setAttribute("aria-busy", "true");
      }

      controls.forEach((btn) => {
        btn.hidden = false;
        btn.setAttribute("disabled", "disabled");
        btn.classList.add("is-pending");
      });

      return;
    }

    if (soldOut) {
      if (input) {
        input.readOnly = true;
        input.disabled = true;
        input.setAttribute("aria-busy", "false");
      }

      controls.forEach((btn) => {
        btn.hidden = false;
        btn.setAttribute("disabled", "disabled");
        btn.classList.remove("is-pending");
      });

      return;
    }

    if (input) {
      input.readOnly = false;
      input.disabled = false;
      input.setAttribute("aria-busy", "false");
    }

    controls.forEach((btn) => {
      btn.hidden = false;
      btn.classList.remove("is-pending");
    });
  }

  function updateQtyButtonUI(root, qty, minQty, maxQty, isSoldOut) {
    if (!root) return;

    const minusBtn = root.querySelector("[data-kouza-qty-minus]");
    const plusBtn = root.querySelector("[data-kouza-qty-plus]");
    const inputId = root.getAttribute("data-qty-input-id");
    const input =
      (inputId && document.getElementById(inputId)) ||
      root.querySelector("[data-kouza-qty-input]");

    if (input) {
      input.disabled = !!isSoldOut;

      if (maxQty != null && maxQty !== "") {
        input.setAttribute("max", String(maxQty));
      } else {
        input.removeAttribute("max");
      }
    }

    if (minusBtn) {
      if (isSoldOut || qty <= minQty) {
        minusBtn.setAttribute("disabled", "disabled");
      } else {
        minusBtn.removeAttribute("disabled");
      }
    }

    if (plusBtn) {
      if (isSoldOut || qty >= maxQty) {
        plusBtn.setAttribute("disabled", "disabled");
      } else {
        plusBtn.removeAttribute("disabled");
      }
    }
  }

  function bindVariantStateObserver(scopeRoot, formRoot, onChange) {
    if (!scopeRoot) return;
    if (scopeRoot.dataset.kouzaVariantObserverBound === "1") return;

    scopeRoot.dataset.kouzaVariantObserverBound = "1";

    let timer = null;
    const targets = getVariantWatchTargets(scopeRoot, formRoot);

    const observer = new MutationObserver((mutations) => {
      const shouldRun = mutations.some((mutation) => {
        const target = mutation.target;
        if (!(target instanceof Element)) return false;

        return (
          isVariantRelatedTarget(target) ||
          isVariantRelatedTarget(target.parentElement)
        );
      });

      if (!shouldRun) return;

      clearTimeout(timer);
      timer = setTimeout(() => {
        onChange();
      }, 0);
    });

    targets.forEach((target) => {
      observer.observe(target, {
        subtree: true,
        attributes: true,
        attributeFilter: [
          "class",
          "aria-selected",
          "aria-checked",
          "aria-pressed",
          "value",
          "checked",
        ],
      });
    });
  }

  function bindQuantityRoot(root) {
    if (!root) return false;
    if (root.dataset.kouzaQtyBound === "1") return true;

    const qtyInputId = root.getAttribute("data-qty-input-id");
    const input =
      (qtyInputId && document.getElementById(qtyInputId)) ||
      root.querySelector("[data-kouza-qty-input]");

    const formAttr = input?.getAttribute("form");
    const formRoot =
      (input && getFormRoot(input)) ||
      (formAttr ? document.getElementById(formAttr) : null);

    const scopeRoot = getVariantScope(root, formRoot);
    const isCartLocked = !!root.querySelector(".kouza-qty__lock-message");

    root.dataset.kouzaQtyBound = "1";

    const minQty = toInt(root.getAttribute("data-min-qty"), 1);

    const rawBaseMax =
      root.getAttribute("data-base-max-qty") ||
      root.getAttribute("data-max-qty");
    const parsedBaseMax =
      rawBaseMax === "" || rawBaseMax == null
        ? HARD_MAX_QTY
        : toInt(rawBaseMax, HARD_MAX_QTY);

    const baseMax = Math.min(parsedBaseMax, HARD_MAX_QTY);

    const variants = parseVariantStockJson(root);
    const variantMap = new Map(
      variants.map((variant) => [String(variant.id), variant]),
    );
    const stockEl = root.querySelector("[data-stock-el]");
    const hasTrackedStock = hasTrackedStockVariant(variants);

    const state = {
      isUpdating: false,
      variantSyncTimer: null,
      pending: true,
      lastStableVariantId: "",
      userInteracted: false,
    };

    function getVariantById(variantId) {
      return variantMap.get(String(variantId || "")) || null;
    }

    function setInputValue(nextQty) {
      if (!input) return false;
      if (String(input.value) === String(nextQty)) return false;

      state.isUpdating = true;
      input.value = String(nextQty);
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
      state.isUpdating = false;

      return true;
    }

    function lockAddToCartByCart() {
      if (!formRoot) return;

      applyAtcReason(formRoot, "cart", true, "");
      applyAtcReason(formRoot, "variant", false, "");
      applyAtcReason(formRoot, "stock", false, "");
    }

    function unlockAddToCartByCart() {
      if (!formRoot) return;
      applyAtcReason(formRoot, "cart", false, "");
    }

    function enterPendingState() {
      state.pending = true;

      if (isCartLocked || !hasTrackedStock) {
        hideStockDisplay(stockEl);
        return;
      }

      renderStockLoading(stockEl);
      setQtyPendingState(root, { loading: true });
      applyAtcReason(formRoot, "stock", false, "");
      applyAtcReason(formRoot, "variant", true, "");
    }

    function applyResolvedVariant(variant, { showAlert = false } = {}) {
      if (!variant) {
        enterPendingState();
        return {
          variant: null,
          dynamicMax: 0,
          qty: input ? toInt(input.value, minQty) : minQty,
          isSoldOut: false,
          isPending: true,
        };
      }

      state.pending = false;
      state.lastStableVariantId = String(variant.id);

      const dynamicMax = calcDynamicMax(baseMax, variant);
      const isSoldOut = dynamicMax <= 0;

      root.setAttribute("data-max-qty", String(dynamicMax));

      if (isCartLocked || !hasTrackedStock) {
        hideStockDisplay(stockEl);
        return {
          variant,
          dynamicMax,
          qty: minQty,
          isSoldOut,
          isPending: false,
        };
      }

      renderStockText(stockEl, variant);

      if (!input || !formRoot) {
        return {
          variant,
          dynamicMax,
          qty: minQty,
          isSoldOut,
          isPending: false,
        };
      }

      let nextQty = toInt(input.value, minQty);

      if (isSoldOut) {
        nextQty = minQty;
      } else {
        if (nextQty < minQty) nextQty = minQty;

        if (nextQty > dynamicMax) {
          if (showAlert) {
            alert("残席数は" + dynamicMax + "です。");
          }
          nextQty = dynamicMax;
        }
      }

      setInputValue(nextQty);

      updateQtyButtonUI(
        root,
        nextQty,
        minQty,
        isSoldOut ? minQty : dynamicMax,
        isSoldOut,
      );
      setQtyPendingState(root, { loading: false, soldOut: isSoldOut });

      applyAtcReason(formRoot, "variant", false, "");
      applyAtcReason(formRoot, "stock", isSoldOut, "");

      return {
        variant,
        dynamicMax,
        qty: nextQty,
        isSoldOut,
        isPending: false,
      };
    }

    function scheduleVariantSync({ fromUserInteraction = false } = {}) {
      if (!pageState.loaded) {
        enterPendingState();
        return;
      }

      if (fromUserInteraction) {
        state.userInteracted = true;
      }

      enterPendingState();

      if (state.variantSyncTimer) {
        clearTimeout(state.variantSyncTimer);
      }

      const startedAt = Date.now();
      let previousVariantId = "";
      let stableCount = 0;

      const tick = () => {
        const currentVariantId = resolveCurrentVariantId(
          scopeRoot,
          formRoot,
          variants,
          root,
          {
            allowInitialFallback: !state.userInteracted,
            preferOptions: state.userInteracted,
          },
        );

        if (currentVariantId && currentVariantId === previousVariantId) {
          stableCount += 1;
        } else {
          stableCount = 0;
        }

        previousVariantId = currentVariantId;

        const resolvedVariant = getVariantById(currentVariantId);
        const timedOut = Date.now() - startedAt > 1500;
        const settled = !!resolvedVariant && stableCount >= 1;

        if (settled) {
          state.variantSyncTimer = null;
          applyResolvedVariant(resolvedVariant, { showAlert: false });
          return;
        }

        if (timedOut) {
          state.variantSyncTimer = null;

          if (resolvedVariant && !state.userInteracted) {
            applyResolvedVariant(resolvedVariant, { showAlert: false });
          } else {
            enterPendingState();
          }
          return;
        }

        state.variantSyncTimer = setTimeout(tick, 80);
      };

      state.variantSyncTimer = setTimeout(tick, 60);
    }

    function applyNonTrackedState({ showAlert = false } = {}) {
      state.pending = false;
      state.lastStableVariantId = "";

      root.setAttribute("data-max-qty", String(baseMax));
      hideStockDisplay(stockEl);

      if (!input || !formRoot) {
        return {
          dynamicMax: baseMax,
          qty: minQty,
          isSoldOut: false,
          isPending: false,
        };
      }

      let nextQty = toInt(input.value, minQty);

      if (nextQty < minQty) nextQty = minQty;

      if (nextQty > baseMax) {
        if (showAlert) {
          alert("最大" + baseMax + "までです。");
        }
        nextQty = baseMax;
      }

      setInputValue(nextQty);

      updateQtyButtonUI(root, nextQty, minQty, baseMax, false);
      setQtyPendingState(root, { loading: false, soldOut: false });

      applyAtcReason(formRoot, "variant", false, "");
      applyAtcReason(formRoot, "stock", false, "");

      return {
        dynamicMax: baseMax,
        qty: nextQty,
        isSoldOut: false,
        isPending: false,
      };
    }

    function changeQtyByNonTracked(delta) {
      if (state.isUpdating || !input) return;

      const currentQty = toInt(input.value, minQty);
      let nextQty = currentQty + delta;

      if (nextQty < minQty) nextQty = minQty;

      if (nextQty > baseMax) {
        alert("最大" + baseMax + "までです。");
        nextQty = baseMax;
      }

      setInputValue(nextQty);
      applyNonTrackedState({ showAlert: false });
    }

    function changeQtyBy(delta) {
      if (state.isUpdating || state.pending || !input) return;

      const currentVariant = getVariantById(state.lastStableVariantId);
      if (!currentVariant) return;

      const dynamicMax = calcDynamicMax(baseMax, currentVariant);
      const isSoldOut = dynamicMax <= 0;
      if (isSoldOut) return;

      const currentQty = toInt(input.value, minQty);
      let nextQty = currentQty + delta;

      if (nextQty < minQty) nextQty = minQty;
      if (nextQty > dynamicMax) {
        alert("残席数は" + dynamicMax + "です。");
        nextQty = dynamicMax;
      }

      setInputValue(nextQty);
      applyResolvedVariant(currentVariant, { showAlert: false });
    }

    if (isCartLocked || !hasTrackedStock) {
      hideStockDisplay(stockEl);
    } else if (stockEl) {
      renderStockLoading(stockEl);
    }

    if (isCartLocked) {
      lockAddToCartByCart();

      if (formRoot) {
        formRoot.addEventListener(
          "submit",
          (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          },
          true,
        );
      }

      return true;
    }

    if (!input || !formRoot) return false;

    if (!hasTrackedStock) {
      unlockAddToCartByCart();
      applyNonTrackedState({ showAlert: false });

      input.addEventListener("change", () => {
        if (state.isUpdating) return;
        applyNonTrackedState({ showAlert: true });
      });

      input.addEventListener("input", () => {
        if (state.isUpdating) return;

        const numeric = String(input.value || "").replace(/[^\d]/g, "");
        if (numeric !== input.value) {
          state.isUpdating = true;
          input.value = numeric;
          state.isUpdating = false;
        }
      });

      const minusBtn = root.querySelector("[data-kouza-qty-minus]");
      const plusBtn = root.querySelector("[data-kouza-qty-plus]");

      if (minusBtn) {
        minusBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          changeQtyByNonTracked(-1);
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          changeQtyByNonTracked(1);
        });
      }

      formRoot.addEventListener(
        "submit",
        (e) => {
          const { dynamicMax, qty, isPending } = applyNonTrackedState({
            showAlert: false,
          });

          if (isPending || qty > dynamicMax) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          }
        },
        true,
      );

      return true;
    }

    if (!input || !formRoot) return false;

    unlockAddToCartByCart();

    setQtyPendingState(root, { loading: true });
    applyAtcReason(formRoot, "stock", false, "");
    applyAtcReason(formRoot, "variant", true, "");

    input.addEventListener("change", () => {
      if (state.isUpdating || state.pending) return;

      const currentVariant = getVariantById(state.lastStableVariantId);
      if (!currentVariant) {
        enterPendingState();
        return;
      }

      applyResolvedVariant(currentVariant, { showAlert: true });
    });

    input.addEventListener("input", () => {
      if (state.isUpdating) return;

      const numeric = String(input.value || "").replace(/[^\d]/g, "");
      if (numeric !== input.value) {
        state.isUpdating = true;
        input.value = numeric;
        state.isUpdating = false;
      }
    });

    const minusBtn = root.querySelector("[data-kouza-qty-minus]");
    const plusBtn = root.querySelector("[data-kouza-qty-plus]");

    if (minusBtn) {
      minusBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        changeQtyBy(-1);
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        changeQtyBy(1);
      });
    }

    scopeRoot.addEventListener(
      "change",
      (e) => {
        if (e.target === input) return;
        if (!isVariantRelatedTarget(e.target)) return;

        scheduleVariantSync({ fromUserInteraction: true });
      },
      true,
    );

    scopeRoot.addEventListener(
      "input",
      (e) => {
        if (e.target === input) return;
        if (!isVariantRelatedTarget(e.target)) return;

        scheduleVariantSync({ fromUserInteraction: true });
      },
      true,
    );

    scopeRoot.addEventListener(
      "click",
      (e) => {
        if (!pageState.loaded) return;
        if (!isVariantRelatedTarget(e.target)) return;

        scheduleVariantSync({ fromUserInteraction: true });
      },
      true,
    );

    bindVariantStateObserver(scopeRoot, formRoot, () => {
      if (!pageState.loaded) return;
      scheduleVariantSync({ fromUserInteraction: true });
    });

    document.addEventListener("variant:change", () => {
      if (!pageState.loaded) return;
      scheduleVariantSync({ fromUserInteraction: true });
    });

    window.addEventListener("popstate", () => {
      if (!pageState.loaded) return;
      scheduleVariantSync({ fromUserInteraction: true });
    });

    document.addEventListener("kouza:page-loaded", () => {
      scheduleVariantSync({ fromUserInteraction: false });
    });

    formRoot.addEventListener(
      "submit",
      (e) => {
        if (state.pending) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }

        const currentVariant = getVariantById(state.lastStableVariantId);
        if (!currentVariant) {
          e.preventDefault();
          e.stopImmediatePropagation();
          enterPendingState();
          return false;
        }

        const { dynamicMax, qty, isSoldOut, isPending } = applyResolvedVariant(
          currentVariant,
          { showAlert: false },
        );

        if (isPending || isSoldOut || qty > dynamicMax) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
      },
      true,
    );

    if (pageState.loaded) {
      scheduleVariantSync({ fromUserInteraction: false });
    }

    return true;
  }

  function scan(node) {
    if (!node || node.nodeType !== 1) return;

    if (node.matches?.("[data-kouza-qty-root]")) {
      bindQuantityRoot(node);
    }

    node.querySelectorAll?.("[data-kouza-qty-root]").forEach(bindQuantityRoot);
  }

  function allBound() {
    const qtyRoots = Array.from(
      document.querySelectorAll("[data-kouza-qty-root]"),
    );
    return qtyRoots.every((el) => el.dataset.kouzaQtyBound === "1");
  }

  function initRetryScan() {
    let tries = 0;

    const timer = setInterval(() => {
      scan(document.documentElement);
      tries += 1;

      if (allBound() || tries >= 30) {
        clearInterval(timer);
      }
    }, 200);
  }

  function init() {
    scan(document.documentElement);
    initRetryScan();

    if (window[OBSERVER_FLAG]) return;
    window[OBSERVER_FLAG] = true;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!node || node.nodeType !== 1) continue;
          scan(node);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();

(function () {
  const RESTRICTED_TAGS = new Set(["kouza", "workshop"]);

  const parseTagsCsv = (csv) =>
    String(csv || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

  const setCheckoutDisabled = (disabled) => {
    document
      .querySelectorAll('button[name="checkout"], .cart__checkout')
      .forEach((btn) => {
        if ("disabled" in btn) btn.disabled = Boolean(disabled);
        btn.setAttribute("aria-disabled", disabled ? "true" : "false");
      });
  };

  const validate = () => {
    const nodes = document.querySelectorAll(
      "[data-aw-cart-item][data-aw-product-tags]",
    );
    const lines = Array.from(nodes).map((el) => {
      const tags = parseTagsCsv(el.dataset.awProductTags);
      return tags.some((t) => RESTRICTED_TAGS.has(t));
    });

    const totalLines = lines.length;
    const restrictedLines = lines.filter(Boolean).length;

    const canCheckout =
      restrictedLines === 0 || (totalLines === 1 && restrictedLines === 1);

    setCheckoutDisabled(!canCheckout);
  };

  const init = () => {
    validate();

    const mo = new MutationObserver(validate);
    const main = document.querySelector("main") || document.body;
    mo.observe(main, { childList: true, subtree: true });

    document.addEventListener("change", validate, true);
    document.addEventListener("click", validate, true);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();

/* =========================================================
  addon-options: チェックボックス付き一括カート追加
========================================================= */
(function () {

  function initAddonOptions(formRoot) {
    if (!formRoot) return;
    if (formRoot.dataset.addonBound === '1') return;
    formRoot.dataset.addonBound = '1';

    var atcBtn = formRoot.querySelector(
      "button.add-to-cart, button[name='add'], [data-add-to-cart]"
    );
    if (!atcBtn) return;

    atcBtn.addEventListener('click', function (e) {

      // 日程未選択ガード
      var datePending = document.documentElement.getAttribute('data-kouza-date-pending');
      if (datePending === 'true') return;

      // チェックされているaddonがなければ通常処理に委譲
      var checked = document.querySelectorAll('.addon-checkbox:checked');
      if (!checked.length) return;

      // aw-product-kouza.jsのdisabled状態を尊重
      if (atcBtn.disabled || atcBtn.classList.contains('is-kouza-disabled')) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      var variantInput = formRoot.querySelector('[name="id"]');
      var qtyInput = formRoot.querySelector('[name="quantity"]');
      if (!variantInput || !variantInput.value) return;

      var items = [{
        id: parseInt(variantInput.value, 10),
        quantity: parseInt(qtyInput ? qtyInput.value : '1', 10)
      }];

      checked.forEach(function (cb) {
        items.push({ id: parseInt(cb.dataset.variantId, 10), quantity: 1 });
      });

      var originalText = atcBtn.textContent;
      atcBtn.disabled = true;
      atcBtn.textContent = '追加中...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart/add failed: ' + r.status);
          return r.json();
        })
        .then(function () {
          window.location.href = '/cart';
        })
        .catch(function (err) {
          console.error('[addon-options] カート追加エラー:', err);
          atcBtn.disabled = false;
          atcBtn.textContent = originalText;
          alert('カートへの追加に失敗しました。もう一度お試しください。');
        });
    });
  }

  function scanAndBind() {
    document.querySelectorAll('form[id*="AddToCartForm"]').forEach(initAddonOptions);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanAndBind, { once: true });
  } else {
    scanAndBind();
  }

  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (!(node instanceof Element)) return;
        if (node.matches('form[id*="AddToCartForm"]')) initAddonOptions(node);
        if (node.querySelectorAll) {
          node.querySelectorAll('form[id*="AddToCartForm"]').forEach(initAddonOptions);
        }
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

})();